import { useEffect, useState, useCallback } from "react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Gauge, Plus, Swords } from "lucide-react";
import { CompetitorForm } from "@/components/competitors/CompetitorForm";
import { CompetitorCard } from "@/components/competitors/CompetitorCard";
import { ComparisonTable } from "@/components/competitors/ComparisonTable";
import { Strengths } from "@/components/competitors/Strengths";
import { Changes } from "@/components/competitors/Changes";
import { Visibility } from "@/components/competitors/Visibility";
import { AiAnalysis } from "@/components/competitors/AiAnalysis";
import { CompetitorSuggestions } from "@/components/competitors/CompetitorSuggestions";
import { VisibilityCompare } from "@/components/competitors/VisibilityCompare";
import { CompetitorMap } from "@/components/competitors/CompetitorMap";

export default function Competitors() {
  const { current } = useEstablishments();
  if (!current) return <div className="p-8 text-slate-500">Aucun établissement sélectionné.</div>;
  return <Inner key={current.id} est={current} />;
}

function Inner({ est }) {
  const [data, setData] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return ["overview", "comparison", "opportunities", "changes", "visibility"].includes(t) ? t : "overview"; });

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`/establishments/${est.id}/competitors`), api.get(`/establishments/${est.id}/competitors/comparison`)]);
    setData(a.data); setCmp(b.data);
  }, [est.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.competitors.some((c) => c.audit_running)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data, load]);

  if (!data || !cmp) return <div className="p-8 grid place-items-center min-h-[60vh]"><Gauge className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  const comps = data.competitors;
  const atLimit = data.limit != null && data.used >= data.limit;
  const anyMeasured = cmp.columns[0].measured && cmp.columns.slice(1).some((c) => c.measured);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="competitors-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Swords className="w-7 h-7 text-blue-600" /> Concurrents & visibilité locale</h1>
          <p className="text-slate-500 text-sm mt-1">Qui sont vos concurrents, ce qu'ils font mieux, ce que vous faites mieux — uniquement à partir de données mesurées.</p>
        </div>
        <div className="text-right">
          <Button onClick={() => setShowForm(!showForm)} disabled={atLimit || data.is_demo} data-testid="add-competitor-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter un concurrent</Button>
          <div className="text-[11px] text-slate-400 mt-1" data-testid="competitor-limit">{data.used} suivi(s){data.limit != null ? ` sur ${data.limit} (abonnement)` : data.unlimited_reason === "admin" ? " · illimité (compte administrateur)" : data.unlimited_reason === "test" ? " · illimité (compte de test)" : " · illimité"}</div>
        </div>
      </div>
      {data.is_demo && <div className="mt-5 rounded-xl bg-amber-50/60 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="competitors-demo-notice">DONNÉES DE DÉMONSTRATION — l'analyse réelle des concurrents n'est pas disponible sur l'établissement démo.</div>}
      {atLimit && <div className="mt-5 rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800" data-testid="competitors-limit-notice">Limite de {data.limit} concurrent(s) atteinte pour votre abonnement.</div>}
      {showForm && <div className="mt-5"><CompetitorForm estId={est.id} onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} /></div>}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="competitors-tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="competitors-tab-comparison">Comparaison</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="competitors-tab-opportunities">Forces & opportunités</TabsTrigger>
          <TabsTrigger value="changes" data-testid="competitors-tab-changes">Changements</TabsTrigger>
          <TabsTrigger value="visibility" data-testid="competitors-tab-visibility">Visibilité Google</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <CompetitorSuggestions estId={est.id} onChanged={load} competitors={comps.filter((c) => (c.status || "active") === "active" && !c.is_demo)} />
          {!data.is_demo && <VisibilityCompare key={comps.length} estId={est.id} />}
          {!data.is_demo && comps.some((c) => !c.is_demo) && <CompetitorMap key={`map-${comps.length}`} estId={est.id} onCompare={() => setTab("comparison")} />}
          {comps.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="no-competitors"><Swords className="w-8 h-8 text-slate-300 mx-auto mb-3" /> Aucun concurrent suivi. Ajoutez un concurrent avec son site web pour l'analyser avec le moteur Respondia.</div>
          ) : comps.map((c) => <CompetitorCard key={c.id} c={c} estId={est.id} onChanged={load} />)}
        </TabsContent>
        <TabsContent value="comparison" className="mt-4 space-y-5">
          <ComparisonTable data={cmp} />
          <AiAnalysis estId={est.id} disabled={!anyMeasured} />
        </TabsContent>
        <TabsContent value="opportunities" className="mt-4"><Strengths strengths={cmp.strengths} /></TabsContent>
        <TabsContent value="changes" className="mt-4"><Changes estId={est.id} monitoring={data.monitoring} isDemo={data.is_demo} onMonitoringChanged={load} /></TabsContent>
        <TabsContent value="visibility" className="mt-4"><Visibility estId={est.id} isDemo={data.is_demo} /></TabsContent>
      </Tabs>
    </div>
  );
}
